import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {
  HTTPWrapperContext,
  RequestConfigImplementation,
  REQUEST_METHODS,
  XHR_STRATEGIES
} from '@ptl/xhr';
import { BrandingConfigurationService } from '@ptl/brand';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { AutocompleteRequestBody, AutocompleteRequestPayload } from '../configs/interfaces/autocomplete/payload.request';
import { ProviderResults } from '../configs/interfaces/autocomplete/payload.response';
import { ProfileRequestPayload } from '../configs/interfaces/profile/payload.request';
import { ProviderDetails } from '../configs/interfaces/profile/payload.response';
import { AllowedAttributeTypes, ATTRIBUTE_TYPES, ClientLocationIdAttribute, PlansAcceptedAttribute, SpecialtyAttribute } from '../configs/interfaces/shared/provider.interface';
import { MemberDataService } from './member-data/member-data.service';
import { UserTokenService } from './user-token/user-token.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';

@Injectable({
  providedIn: 'root'
})
export class DoctorInformationService {

  constructor(
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private memberDataService: MemberDataService,
    private userTokenService: UserTokenService,
    private commonUtilityService: CommonUtilityService
  ) { }

  /**
   * Autocomplete API
   * Response types:
   *  400	Missing payload/query parameter	Provider search is experiencing some difficulty. Please try again later. Received status code: 400
   *  401, 403, 500	Token error/Axway problem/Kyruus down	Provider search is experiencing some difficulty. Please try again later. Received status code: <received-status-code>
   */
  private async getKyruusAutocompleteResults(lineOfBusiness: LINE_OF_BUSINESS_ENUM, payloadBody: AutocompleteRequestBody): Promise<ProviderResults> {

    const payload: AutocompleteRequestPayload = {
      body: payloadBody,
      member: await this.memberDataService.getMemberData(lineOfBusiness, payloadBody.location)
    }

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/provider/v1/kyruus/healthsparq/service/v4/search`,
      headers: new HttpHeaders({
        "User-Token": this.userTokenService.getKyruusLoginToken(),
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      strategyConfigs: { forceModernUrl: true },
      POST_OR_PUT_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<ProviderResults>(requestConfigImplementation);
  }

  /** Wrapper function - invokes session token validation before making the request */
  async autocompleteSearch(configs: {
    lob: LINE_OF_BUSINESS_ENUM,
    kyruusConfigs: AutocompleteRequestBody
   } ) {

    try {
      await this.userTokenService.validateSession(configs.lob, configs.kyruusConfigs.location);
      return this.getKyruusAutocompleteResults(configs.lob, configs.kyruusConfigs)
    } catch (e) {
      console.error(e);
    }

  }

  /** 
   * Detail API:
   * Response types:
   *  400	Missing payload/query parameter	Provider search is experiencing some difficulty. Please try again later. Received status code: 400
   *  401, 403, 500	Token error/Axway problem/Kyruus down	Provider search is experiencing some difficulty. Please try again later. Received status code: <received-status-code
   */
  private async getKyruusProfileResults(payloadBody: ProfileRequestPayload): Promise<ProviderDetails> {
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/provider/v1/kyruus/healthsparq/service/v2/profile`,
      headers: new HttpHeaders({
        "User-Token": this.userTokenService.getKyruusLoginToken(),
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      strategyConfigs: { forceModernUrl: true },
      GET_PAYLOAD: payloadBody
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<ProviderDetails>(requestConfigImplementation);
  }

  /** Wrapper function - invokes session token validation before making the request */
  async profileSearch(lob: LINE_OF_BUSINESS_ENUM, profileParams: ProfileRequestPayload) {

    try {
      await this.userTokenService.validateSession(lob, profileParams.location);
      return this.getKyruusProfileResults(profileParams)
    } catch (e) {
      console.error(e);
    }

  }

  /** Data helpers */
  private getProviderAttribute(provider: { attributes: AllowedAttributeTypes[] }, attributeType: ATTRIBUTE_TYPES): AllowedAttributeTypes {
    const matchedAttributeType = provider.attributes.filter( obj => obj.key === attributeType );
    if (matchedAttributeType.length) {
      return matchedAttributeType[0];
    }
  }
  getSpecialtyAttribute(provider: { attributes: AllowedAttributeTypes[] }): SpecialtyAttribute {
    const specialtyAttribute: SpecialtyAttribute = this.getProviderAttribute(provider, ATTRIBUTE_TYPES.SPECIALTY) as SpecialtyAttribute;
    if (specialtyAttribute) {
      return specialtyAttribute;
    }
  }
  getClientLocationIdAttribute(provider: { attributes: AllowedAttributeTypes[] }): ClientLocationIdAttribute {
    const clientLocationIdAttribute: ClientLocationIdAttribute = this.getProviderAttribute(provider, ATTRIBUTE_TYPES.CLIENT_LOCATION_ID) as ClientLocationIdAttribute;
    if (clientLocationIdAttribute) {
      return clientLocationIdAttribute;
    }
  }
  getPlansAcceptedAttribute(provider: { attributes: AllowedAttributeTypes[] }): PlansAcceptedAttribute {
    const plansAcceptedAttribute: PlansAcceptedAttribute = this.getProviderAttribute(provider, ATTRIBUTE_TYPES.PLANS_ACCEPTED) as PlansAcceptedAttribute;
    if (plansAcceptedAttribute) {
      return plansAcceptedAttribute;
    }
  }
}
